import * as React from 'react'
import { ArrowLeftIcon } from './icons'
import { GoodPartyOrgLogo } from './good-party-org-logo'
import { Button } from './button'
import { IconButton } from './icon-button'
import { Separator } from './separator'
import { cn } from '@styleguide/lib/utils'

export interface PageHeaderProps extends React.HTMLAttributes<HTMLElement> {
  /** Main bar: section heading. */
  heading?: React.ReactNode
  /**
   * Main bar: overrides the left slot (default: GoodParty logo, hidden on desktop via lg:hidden).
   * When a custom leading is passed, breakpoint visibility and the separator are the consumer's responsibility.
   */
  leading?: React.ReactNode
  /** Main bar: mobile-only right slot — burger trigger. Hidden on desktop (lg:hidden). */
  trailing?: React.ReactNode
  /**
   * Sub-bar (optional strip below the main bar).
   * Renders automatically when onBack, backHref, subBarTrailing, or subBarContent is provided.
   */
  onBack?: () => void
  /**
   * Sub-bar: href for the back button (renders an <a> tag).
   * If both backHref and onBack are provided, backHref takes precedence and onBack is ignored.
   */
  backHref?: string
  /** Sub-bar: label shown next to the back arrow (e.g. section name). */
  backLabel?: string
  /** Sub-bar: right slot — action buttons. */
  subBarTrailing?: React.ReactNode
  /** Sub-bar: center slot content (between back button and actions). */
  subBarContent?: React.ReactNode
  /** Main bar: overrides merged onto the bar itself (height, background, border). */
  barClassName?: string
  /**
   * Main bar: overrides merged onto the inner content wrapper — e.g.
   * `mx-auto max-w-[608px]` to align the heading with a constrained page column.
   */
  contentClassName?: string
}

/**
 * Dashboard nav pages don't use it: they set DashboardLayout's navHeader so the
 * title matches the sidebar tab. A page the user steps into drops the sidebar
 * and opens on a back arrow and its title (learned rule 27).
 *
 * @summary Use for a sticky bar with the page title, plus an optional sub-bar
 * for a back arrow and actions. For a trail of parent pages, use BreadcrumbNav.
 */
function PageHeader({
  heading,
  leading,
  trailing,
  onBack,
  backHref,
  backLabel,
  subBarTrailing,
  subBarContent,
  barClassName,
  contentClassName,
  className,
  ...props
}: PageHeaderProps) {
  const hasSubBar = !!(onBack ?? backHref ?? subBarTrailing ?? subBarContent)

  const backEl = backHref ? (
    backLabel ? (
      <Button variant="ghost" size="small" asChild>
        <a href={backHref}>
          <ArrowLeftIcon />
          {backLabel}
        </a>
      </Button>
    ) : (
      <IconButton variant="ghost" size="small" asChild aria-label="Go back">
        <a href={backHref}>
          <ArrowLeftIcon />
        </a>
      </IconButton>
    )
  ) : onBack ? (
    backLabel ? (
      <Button
        variant="ghost"
        size="small"
        icon={<ArrowLeftIcon />}
        onClick={onBack}
      >
        {backLabel}
      </Button>
    ) : (
      <IconButton
        variant="ghost"
        size="small"
        onClick={onBack}
        aria-label="Go back"
      >
        <ArrowLeftIcon />
      </IconButton>
    )
  ) : null

  const leadingEl =
    leading !== undefined ? (
      leading
    ) : (
      <GoodPartyOrgLogo size="small" className="shrink-0 lg:hidden" />
    )

  return (
    <header
      data-slot="page-header"
      className={cn('flex flex-col w-full sticky top-0 z-10', className)}
      {...props}
    >
      <div
        data-slot="page-header-bar"
        className={cn(
          'flex items-center h-16 px-4 bg-sidebar border-b border-sidebar-border',
          barClassName,
        )}
      >
        <div
          data-slot="page-header-content"
          className={cn('flex w-full min-w-0 items-center', contentClassName)}
        >
          {leadingEl && (
            <>
              <div className="flex items-center shrink-0">{leadingEl}</div>
              {heading && (
                <Separator
                  orientation="vertical"
                  className="data-[orientation=vertical]:h-5 mx-3 lg:hidden"
                />
              )}
            </>
          )}

          {heading && (
            <div className="flex-1 min-w-0">
              <h1 className="block truncate text-base font-semibold text-sidebar-foreground m-0">
                {heading}
              </h1>
            </div>
          )}

          {trailing && (
            <div className="flex items-center shrink-0 ml-auto lg:hidden">
              {trailing}
            </div>
          )}
        </div>
      </div>

      {hasSubBar && (
        <div
          data-slot="page-header-sub-bar"
          className="flex items-center gap-2 h-12 px-4 bg-sidebar border-b border-sidebar-border"
        >
          {backEl && <div className="flex items-center shrink-0">{backEl}</div>}

          {subBarContent && (
            <div className="flex-1 min-w-0">{subBarContent}</div>
          )}

          {subBarTrailing && (
            <div className="flex items-center gap-2 shrink-0 ml-auto">
              {subBarTrailing}
            </div>
          )}
        </div>
      )}
    </header>
  )
}

export { PageHeader }
